import React, { useState, useEffect } from 'react';
import { 
  LockClosedIcon, 
  ArrowDownTrayIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const Tooltip = ({ children, content }: { children: React.ReactNode, content: string }) => (
  <div className="relative flex items-center group/tooltip">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/tooltip:block z-50 whitespace-nowrap bg-slate-800 text-white text-[11px] font-medium px-2.5 py-1.5 rounded shadow-lg pointer-events-none opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200">
      {content}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
    </div>
  </div>
);

// Skeleton Row Component
const LoadingSkeleton = () => {
  // Generate 7 blank rows to match the mock data length
  return (
    <>
      {[...Array(7)].map((_, i) => (
        <tr key={i} className="animate-pulse border-b border-slate-100">
          <td className="px-4 py-3 border-r border-slate-200">
            <div className="flex items-center gap-3">
               <div className="h-4 w-4 bg-slate-200 rounded"></div>
               <div className="h-6 w-6 rounded-full bg-slate-200"></div>
               <div className="h-4 w-32 bg-slate-200 rounded"></div>
            </div>
          </td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-40 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200 flex items-center gap-2">
            <div className="w-4 h-4 bg-slate-200 rounded-sm"></div>
            <div className="h-4 w-32 bg-slate-200 rounded"></div>
          </td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-6 w-20 bg-slate-200 rounded-md"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-6 w-24 bg-slate-200 rounded-md"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-20 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-24 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-6 w-8 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-32 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3"><div className="h-6 w-20 bg-slate-200 rounded"></div></td>
        </tr>
      ))}
    </>
  );
};

const mockLeadsData = [
  { id: 1, name: 'Yogeshwar Bhangale', title: 'International Business Development Executive', company: 'Prophecus Technologies', email: 'No email', mobile: 'Reveal mobile', country: 'India', city: 'Pune', empCount: '21', industry: 'IT Services and IT Consulting' },
  { id: 2, name: 'Dipanjana Ghosh', title: 'Client Partner', company: 'Prophecus Technologies', email: 'No email', mobile: 'Reveal mobile', country: 'India', city: 'Kolkata', empCount: '21', industry: 'IT Services and IT Consulting' },
  { id: 3, name: 'Dawit Diriba', title: 'Full Stack Engineer', company: 'Prophecus Technologies', email: 'No email', mobile: 'No mobile', country: 'Ethiopia', city: 'Addis Ababa', empCount: '21', industry: 'IT Services and IT Consulting' },
  { id: 4, name: 'Md Sabbir Ahmed', title: 'Business Development Executive', company: 'Prophecus Technologies', email: 'No email', mobile: 'No mobile', country: 'Bangladesh', city: '-', empCount: '21', industry: 'IT Services and IT Consulting' },
  { id: 5, name: 'Yeabsira Ashenafi', title: 'Senior Back End Developer', company: 'Prophecus Technologies', email: 'No email', mobile: 'No mobile', country: 'South Africa', city: 'Cape Town', empCount: '21', industry: 'IT Services and IT Consulting' },
  { id: 6, name: 'Gandham Lasika', title: 'Intern', company: 'Prophecus Technologies', email: 'No email', mobile: 'No mobile', country: 'India', city: 'Vijayawada East', empCount: '21', industry: 'IT Services and IT Consulting' },
  { id: 7, name: 'Karthik Varma', title: 'CEO', company: 'Prophecus Technologies', email: 'No email', mobile: 'No mobile', country: 'India', city: 'Vijayawada East', empCount: '21', industry: 'IT Services and IT Consulting' },
];

export default function LeadTable() {
  const [selectedAll, setSelectedAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<typeof mockLeadsData>([]);

  // Simulate API fetch delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setLeads(mockLeadsData);
      setLoading(false);
    }, 3000); // 3 seconds delay

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col w-full h-fit overflow-hidden">
      
      {/* Table Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
            checked={selectedAll}
            onChange={() => setSelectedAll(!selectedAll)}
          />
          <span className="text-sm font-medium text-slate-700">0 selected <span className="text-slate-400 ml-1">▼</span></span>
        </div>
        
        <div className="flex items-center gap-4 text-sm font-medium text-slate-600">
          <button className="flex items-center gap-1.5 hover:text-brand-700 transition-colors">
            <PlusIcon className="w-4 h-4" /> Add to list
          </button>
          <button className="flex items-center gap-1.5 hover:text-brand-700 transition-colors">
            Push to...
          </button>
          <button className="flex items-center gap-1.5 hover:text-brand-700 transition-colors">
            <ArrowDownTrayIcon className="w-4 h-4" /> Export
          </button>
          <button className="flex items-center gap-1.5 text-slate-900 font-semibold transition-colors">
            <MagnifyingGlassIcon className="w-4 h-4" /> Saved Searches
          </button>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div className="overflow-x-auto bg-white">
        <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 border-b border-slate-200 uppercase tracking-wider">
              <th className="sticky left-0 top-0 z-20 bg-slate-50 px-4 py-3 font-semibold border-r border-slate-200 min-w-[280px]">
                <div className="flex items-center gap-3">
                  <span className="w-4"></span>
                  Name
                </div>
              </th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Title</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Company</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Email</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Mobile</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Country</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">City</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Employee Count</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Industry</th>
              <th className="px-4 py-3 font-semibold">In Lists</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
            {loading ? (
              <LoadingSkeleton />
            ) : (
              leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-brand-50 transition-colors duration-150 group cursor-pointer">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-brand-50 px-4 py-3 border-r border-slate-200 transition-colors duration-150">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer" />
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 group-hover:bg-white transition-colors">
                          {lead.name.charAt(0)}
                        </div>
                        <span className="font-semibold text-slate-900">{lead.name}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200">{lead.title}</td>
                  <td className="px-4 py-3 border-r border-slate-200 flex items-center gap-2">
                    <Tooltip content="Prophecus Technologies (Verified)">
                      <div className="w-4 h-4 bg-red-800 rounded-sm text-[8px] flex items-center justify-center text-white font-bold hover:scale-110 transition-transform">P</div>
                    </Tooltip>
                    {lead.company}
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 group-hover:bg-white transition-colors">
                      {lead.email}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200">
                    {lead.mobile === 'Reveal mobile' ? (
                      <Tooltip content="Use 1 credit to reveal mobile number">
                        <button className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:border-brand-400 hover:text-brand-700 shadow-sm transition-all">
                          <LockClosedIcon className="w-3 h-3 text-slate-400" />
                          {lead.mobile}
                        </button>
                      </Tooltip>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 group-hover:bg-white transition-colors">
                        {lead.mobile}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200">{lead.country}</td>
                  <td className="px-4 py-3 border-r border-slate-200">{lead.city}</td>
                  <td className="px-4 py-3 border-r border-slate-200">
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200 group-hover:bg-white transition-colors">
                      {lead.empCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200 text-slate-500 truncate max-w-[200px]">{lead.industry}</td>
                  <td className="px-4 py-3">
                    <button className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-brand-700 border border-transparent hover:border-brand-200 hover:bg-white px-2 py-1 rounded transition-all">
                      <PlusIcon className="w-3 h-3" /> Add to list
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white text-sm text-slate-500 shrink-0">
        <span>1 - {loading ? 0 : leads.length} of {loading ? 0 : 13}</span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <button className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 transition-colors" disabled={loading}>&lt;</button>
            <button className="w-6 h-6 flex items-center justify-center rounded bg-brand-50 text-brand-700 font-medium" disabled={loading}>1</button>
            <button className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 transition-colors" disabled={loading}>&gt;</button>
          </div>
          <span>Go to page <input type="text" className="w-10 border border-slate-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none rounded px-1.5 py-0.5 text-center text-slate-900 transition-all" defaultValue="1" disabled={loading} /></span>
        </div>
      </div>
    </div>
  );
}